/**
 * View Limit Gating System
 * Tracks ad views for non-authenticated users and prompts signup after 16 views
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ViewLimitModal } from '@/components/ViewLimitModal';

const ViewLimitContext = createContext<any>(null);

const STORAGE_KEY = 'obyect_ad_views';
const THRESHOLD = 16; // Free users can view 16 ads before being prompted to sign up

/**
 * Provides view limit tracking for non-authenticated users.
 * Enforces a 16-view threshold before prompting users to sign up.
 * View counts are persisted to localStorage and survive page refreshes.
 *
 * @param props - Component props
 * @param props.children - Child components to wrap with view limit context
 */
export const ViewLimitProvider = ({ children }: { children: React.ReactNode }) => {
  const [count, setCount] = useState(0);
  const [showModal, setShowModal] = useState(false);

  // TODO: Initialize state from localStorage using STORAGE_KEY
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsedCount = stored ? parseInt(stored, 10) : 0;
    setCount(isNaN(parsedCount) ? 0 : parsedCount);
  }, []);

  // TODO: increment(): increase count, persist to localStorage, if count >= THRESHOLD then open modal
  const increment = async () => {
    const newCount = count + 1;
    localStorage.setItem(STORAGE_KEY, String(newCount));
    setCount(newCount);
    
    if (newCount >= THRESHOLD) {
      setShowModal(true);
    }
  };

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const value = useMemo(() => ({
    count,
    remaining: Math.max(THRESHOLD - count, 0),
    threshold: THRESHOLD,
    showModal,
    increment,
    openModal,
    closeModal
  }), [count, showModal]);

  return (
    <ViewLimitContext.Provider value={value}>
      {children}
      {showModal && <ViewLimitModal threshold={THRESHOLD} onClose={closeModal} />}
    </ViewLimitContext.Provider>
  );
};

/**
 * Hook to access view limit tracking functionality.
 * Must be used within a ViewLimitProvider.
 *
 * @returns View limit context values and methods
 * @returns count - Current number of ad views
 * @returns remaining - Number of views remaining before hitting threshold
 * @returns threshold - Maximum allowed ad views (16)
 * @returns showModal - Whether the signup modal is currently displayed
 * @returns increment - Function to increment view count and show modal if threshold reached
 * @returns openModal - Function to manually open the signup modal
 * @returns closeModal - Function to close the signup modal
 *
 * @example
 * ```tsx
 * function AdCard() {
 *   const { increment, remaining } = useViewLimit();
 *
 *   const handleAdClick = async () => {
 *     await increment(); // Track the view
 *     // Views remaining: ${remaining}
 *   };
 *
 *   return <button onClick={handleAdClick}>View Ad</button>;
 * }
 * ```
 *
 * @throws Error if used outside of ViewLimitProvider
 */
export const useViewLimit = () => {
  const context = useContext(ViewLimitContext);
  if (!context) {
    throw new Error('useViewLimit must be used within ViewLimitProvider');
  }
  return context;
};